import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
	DistrictSelector,
	ToggleButton,
	CustomTable,
	DistrictMap,
} from '@components';
import { Tooltip } from 'flowbite-react';
import { useParams } from 'react-router-dom';
import { HousingOptionsCard } from '@/pages/districtDashboard/components';
import { PiExclamationMarkBold } from 'react-icons/pi';
import { HousingOption } from '@interface';
import { useDispatch, useSelector } from 'react-redux';
import { fatchDistrictProperty } from '@/store/districtSlice';
import { AppDispatch, RootState } from '@store';
import {
	applyFilters,
	checkActiveFilters,
	processAndTransformHousingData,
	updatePropertiesCount,
} from '@utils';
import {
	housingOptionsInitalData,
	initialPropertyData,
	thresholdsOptions,
} from '@mockData';

enum ViewMode {
	MAP = 'MAP',
	TABLE = 'TABLE',
}

interface ProcessingState {
	isLoading: boolean;
	error: string | null;
}

export const DistrictDashboard: React.FC = () => {
	const { module, id } = useParams<{ module: string; id: string }>();
	const dispatch = useDispatch<AppDispatch>();
	const { data } = useSelector((state: RootState) => state.rootSlice.district);

	// Processing state
	const [processingState, setProcessingState] = useState<ProcessingState>({
		isLoading: false,
		error: null
	});

	// Core data state
	const [housingOptions, setHousingOptions] = useState<HousingOption[]>(housingOptionsInitalData);
	const [selectedHousingType, setSelectedHousingType] = useState('All Residential Housing');
	
	// Display state
	const [districtData, setDistrictData] = useState<any[]>([]);
	const [view, setView] = useState(ViewMode.MAP);
	
	// Filter state
	const [filterType, setFilterType] = useState<'AND' | 'OR'>('AND');
	const [toggleStates, setToggleStates] = useState<Record<string, boolean>>({});
	const [dataInterval, setDataInterval] = useState('30_days');
	const [thresholds, setThresholds] = useState<Record<string, number>>(thresholdsOptions);
	const [properties, setProperties] = useState<Record<string, number>>(initialPropertyData);

	// Refs for tracking state changes and preventing unnecessary updates
	const lastProcessedFilters = useRef<string>('');
	const processingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);

	// Memoized current housing data
	const currentHousingData = useMemo(() => {
		return housingOptions.find(item => item.label === selectedHousingType) || {
			data: [],
			units: 0,
			dataCount: 0
		};
	}, [housingOptions, selectedHousingType]);

	// Memoized counts for total (unfiltered) data
	const totalCounts = useMemo(() => ({
		properties: currentHousingData.dataCount || 0,
		units: currentHousingData.units || 0
	}), [currentHousingData]);

	// Memoized current counts for filtered data
	const currentCounts = useMemo(() => {
		const units = districtData.reduce((sum, item) => sum + (item['Residential Units'] || 0), 0);
		return {
			properties: districtData.length,
			units
		};
	}, [districtData]);

	// Reset function
	const resetState = useCallback(() => {
		// Cancel any ongoing processing
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}
		if (processingTimeoutRef.current) {
			clearTimeout(processingTimeoutRef.current);
		}

		setDistrictData([]);
		setSelectedHousingType('All Residential Housing');
		setView(ViewMode.MAP);
		setHousingOptions(housingOptionsInitalData);
		setFilterType('AND');
		setToggleStates({});
		setDataInterval('30_days');
		setThresholds(thresholdsOptions);
		setProperties(initialPropertyData);
		setProcessingState({ isLoading: true, error: null });
		lastProcessedFilters.current = '';
	}, []);

	// Fetch district property when ID changes
	useEffect(() => {
		if (module && id) {
			resetState();
			dispatch(fatchDistrictProperty({ module, id }));
		}
	}, [id, module, dispatch, resetState]);

	// Process initial data when fetched
	useEffect(() => {
		if (!data) return;

		const processData = async () => {
			setProcessingState({ isLoading: true, error: null });
			
			try {
				// Use setTimeout to prevent UI blocking during large data processing
				const categories = await new Promise<HousingOption[]>((resolve) => {
					setTimeout(() => {
						try {
							const result = processAndTransformHousingData(data);
							resolve(result);
						} catch (error) {
							throw error;
						}
					}, 0);
				});

				setHousingOptions(categories);
				
				// Set initial data without additional processing
				const firstCategory = categories[0] || { data: [], units: 0, dataCount: 0 };
				setDistrictData(firstCategory.data || []);
				
				setProcessingState({ isLoading: false, error: null });
			} catch (error) {
				setProcessingState({ 
					isLoading: false, 
					error: 'Failed to process housing data' 
				});
				console.error('Error processing housing data:', error);
			}
		};

		processData();
	}, [data]);

	// Debounced filter application
	const applyFiltersDebounced = useCallback(async (
		baseData: any[],
		activeFilters: any[],
		filterType: 'AND' | 'OR',
		dataInterval: string,
		signal?: AbortSignal
	) => {
		// Create a unique key for current filter state
		const filterKey = JSON.stringify({
			selectedHousingType,
			activeFilters,
			filterType,
			dataInterval
		});

		// Skip if we've already processed these exact filters
		if (filterKey === lastProcessedFilters.current) {
			return;
		}

		setProcessingState({ isLoading: true, error: null });

		try {
			if (activeFilters.length === 0) {
				// No filters - use base data
				setDistrictData(baseData);
				setProperties(updatePropertiesCount(baseData, thresholds, dataInterval));
			} else {
				// Apply filters asynchronously with abort support
				const filteredData = await applyFilters(baseData, activeFilters, filterType, dataInterval);
				
				// Check if operation was aborted
				if (signal?.aborted) {
					return;
				}

				setDistrictData(filteredData);
				setProperties(updatePropertiesCount(filteredData, thresholds, dataInterval));
			}

			lastProcessedFilters.current = filterKey;
			setProcessingState({ isLoading: false, error: null });
		} catch (error) {
			if (!signal?.aborted) {
				setProcessingState({ 
					isLoading: false, 
					error: 'Failed to apply filters' 
				});
				console.error('Error applying filters:', error);
			}
		}
	}, [selectedHousingType, thresholds]);

	// Main filter effect with debouncing and abort control
	useEffect(() => {
		// Cancel previous processing
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}
		if (processingTimeoutRef.current) {
			clearTimeout(processingTimeoutRef.current);
		}

		// Create new abort controller
		const abortController = new AbortController();
		abortControllerRef.current = abortController;

		// Debounce the filter application
		processingTimeoutRef.current = setTimeout(() => {
			const baseData = currentHousingData.data;
			const activeFilters = checkActiveFilters(toggleStates, thresholds);

			applyFiltersDebounced(
				baseData,
				activeFilters,
				filterType,
				dataInterval,
				abortController.signal
			);
		}, 150); // 150ms debounce

		// Cleanup function
		return () => {
			if (processingTimeoutRef.current) {
				clearTimeout(processingTimeoutRef.current);
			}
			abortController.abort();
		};
	}, [
		currentHousingData.data,
		toggleStates,
		thresholds,
		filterType,
		dataInterval,
		applyFiltersDebounced
	]);

	// Optimized handlers with proper state updates
	const handleToggle = useCallback((id: string) => {
		setToggleStates(prev => ({
			...prev,
			[id]: !prev[id]
		}));
	}, []);

	const handleThresholdChange = useCallback((id: string, value: number) => {
		const newValue = isNaN(value) || value < 0 ? 0 : value;
		setThresholds(prev => ({
			...prev,
			[id]: newValue
		}));
	}, []);

	const handleDataIntervalChange = useCallback((interval: string) => {
		setDataInterval(interval);
	}, []);

	const handleViewChange = useCallback(() => {
		setView(prevView => 
			prevView === ViewMode.MAP ? ViewMode.TABLE : ViewMode.MAP
		);
	}, []);

	const handleHousingViewChange = useCallback((value: string) => {
		setSelectedHousingType(value);
		setToggleStates({}); // Reset toggles when housing type changes
		// Clear last processed filters to force reprocessing
		lastProcessedFilters.current = '';
	}, []);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
			if (processingTimeoutRef.current) {
				clearTimeout(processingTimeoutRef.current);
			}
		};
	}, []);

	return (
		<div className="flex flex-col md:flex-row w-full">
			<div className="relative flex-none transition-all duration-300 w-full md:w-[333px]">
				<HousingOptionsCard
					selectedHousingType={selectedHousingType}
					handleHousnigViewChange={handleHousingViewChange}
					housingOptions={housingOptions}
					thresholds={thresholds}
					handleThresholdChange={handleThresholdChange}
					properties={properties}
					dataInterval={dataInterval}
					handleDataIntervalChange={handleDataIntervalChange}
					filterType={filterType}
					setFilterType={setFilterType}
					toggleStates={toggleStates}
					handleToggle={handleToggle}
				/>
			</div>

			<div className="transition-all duration-300 w-full md:w-[calc(100%-333px)] px-4 md:pr-4 mb-10">
				<div className="py-4 flex flex-col md:flex-row w-full items-center gap-3">
					<DistrictSelector module={module} id={id} />
					<div className="text-sm font-semibold">
						Access more options with a{' '}
						<label className="text-link">Custom Search</label>
					</div>
				</div>
				
				<div className="p-4 mb-4 flex flex-wrap items-center md:gap-x-6 border border-opacity-50 border-black bg-[#1465b7] text-white">
					<h1 className="text-lg font-bold uppercase">{selectedHousingType}</h1>
					<div className="inline-flex gap-3 text-sm">
						<div>
							Properties: <b>{totalCounts.properties}</b>
						</div>
						<div>
							Units: <b>{totalCounts.units}</b>
						</div>
					</div>
				</div>
				
				<div className="border border-gray-700 py-2 px-4 w-full mb-4">
					<div className="flex items-center justify-between mb-2">
						<div className="flex items-center gap-1 text-sm">
							<span>Displaying</span>
							<select
								className="outline-none underline hover:text-link font-bold cursor-pointer pl-1 appearance-none"
								value={selectedHousingType}
								onChange={(e) => handleHousingViewChange(e.target.value)}
								disabled={processingState.isLoading}
							>
								{housingOptions.map((option) => (
									<option key={option.value} value={option.label}>
										{option.label}
									</option>
								))}
							</select>
						</div>
					</div>
					
					<div className="text-sm font-bold flex flex-wrap items-center space-x-2">
						<span>
							Properties: <b>{currentCounts.properties}</b>
						</span>
						<span>
							Units: <b>{currentCounts.units}</b>
						</span>
						{processingState.isLoading && (
							<span className="text-blue-600 text-xs">Processing...</span>
						)}
						{processingState.error && (
							<span className="text-red-600 text-xs">{processingState.error}</span>
						)}
						<Tooltip content="Tip: Apply one or more Datasets to narrow down results">
							<PiExclamationMarkBold className="ml-2 bg-[#1465b7] hover:btn-dark cursor-pointer text-white p-1 rounded-full text-lg font-bold" />
						</Tooltip>
					</div>
				</div>
				
				<div className="flex justify-end mt-3">
					<ToggleButton
						label="MAP"
						isActive={view === ViewMode.MAP}
						onClick={handleViewChange}
					/>
					<ToggleButton
						label="TABLE"
						isActive={view === ViewMode.TABLE}
						onClick={handleViewChange}
					/>
				</div>
				
				<div className="mt-4">
					{processingState.isLoading ? (
						<div className="flex justify-center items-center py-8">
							<div className="text-sm text-gray-600">Loading data...</div>
						</div>
					) : view === ViewMode.MAP ? (
						<div>
							<DistrictMap locations={districtData} />
						</div>
					) : (
						<CustomTable
							dataInterval={dataInterval}
							data={districtData}
							headerClass="mb-4"
						/>
					)}
				</div>
			</div>
		</div>
	);
};